import type { GeometryElement as JXGElement } from 'jsxgraph';
import type { PolygonResult, AngleResult, LineLabelConfig, AngleLabelConfig } from '@examdojo/core/widget';
import type { ThemeColorName } from '@examdojo/styles';
import type { PortMap, PointPort, EdgePort } from '../../../port';
import { computeAngleCenter } from '../../../port';
import type { SolvedTriangle } from '../../../analytics';
import type { PlacedShape } from '../../../placement';
import type { GeometryPadElementContext, CreatedGeometryElement } from '../../element.model';
import {
  resolveGeometryStyle,
  resolveGeometryLabelColor,
  resolveSubpartLabelOverride,
  resolveAngleLabelOverride,
  computeAngleScope,
  computeEdgeScope,
} from '../../coloring';
import {
  initShapeCreatorContext,
  HIDDEN_POINT_ATTRS,
  resolveBaseVertexStyle,
  buildVertexPerIndex,
} from '../shape-creator.util';
import type { TriangleElement } from './triangle.model';
import type { TriangleAngle } from './triangle.model';
import { TRIANGLE_VERTEX_NAMES } from './triangle.model';

const EPSILON = 1e-9;

/**
 * Create a triangle element on the JSXGraph board.
 *
 * Creates:
 * - A polygon (3 vertices + 3 edges)
 * - Angle markers for right angles and given angles
 * - Labels on sides and angles
 *
 * Registers ports: point.A/B/C, edge.AB/BC/CA, a/b/c, alpha/beta/gamma, area, perimeter
 */
export function createTriangle(
  element: TriangleElement & { id: string },
  placed: PlacedShape,
  solved: SolvedTriangle,
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const ctx = initShapeCreatorContext(element, context);
  const { elementFactory, geometryConfig, elementDisplay, board, jsxObjects, labels, styleCtx } = ctx;
  const givenKeys = new Set(Object.keys(element.params));

  // Get world-space vertex coordinates
  const vA = placed.vertices['A'] as [number, number];
  const vB = placed.vertices['B'] as [number, number];
  const vC = placed.vertices['C'] as [number, number];

  // --- Detect right angle for Thales' theorem constraint ---
  // If a vertex has a 90° angle, it becomes a glider on the Thales circle
  // (circle with the opposite edge as diameter), so the right angle is
  // preserved during interaction.
  const rightAngleVertex = detectRightAngle(solved);
  let vertices: [JXGElement, JXGElement, JXGElement];
  // For translation: only free points (not gliders/computed).
  // Gliders follow automatically when their parent geometry updates.
  let freePoints: JXGElement[];

  if (rightAngleVertex) {
    const coords = { A: vA, B: vB, C: vC };
    const result = createThalesConstrained(board, coords, rightAngleVertex);
    vertices = result.vertices;
    freePoints = result.freePoints;
    jsxObjects.push(...result.helpers);
  } else {
    vertices = [
      board.create('point', vA, { ...HIDDEN_POINT_ATTRS, name: 'A' }) as JXGElement,
      board.create('point', vB, { ...HIDDEN_POINT_ATTRS, name: 'B' }) as JXGElement,
      board.create('point', vC, { ...HIDDEN_POINT_ATTRS, name: 'C' }) as JXGElement,
    ];
    freePoints = [...vertices];
  }

  // --- Resolve per-edge styles ---
  const edgeABStyle = resolveGeometryStyle('line', 'Triangle', styleCtx, elementDisplay, 'AB');
  const edgeBCStyle = resolveGeometryStyle('line', 'Triangle', styleCtx, elementDisplay, 'BC');
  const edgeCAStyle = resolveGeometryStyle('line', 'Triangle', styleCtx, elementDisplay, 'CA');
  const labelColorAB = resolveGeometryLabelColor('Triangle', styleCtx, elementDisplay, 'AB') as ThemeColorName;
  const labelColorBC = resolveGeometryLabelColor('Triangle', styleCtx, elementDisplay, 'BC') as ThemeColorName;
  const labelColorCA = resolveGeometryLabelColor('Triangle', styleCtx, elementDisplay, 'CA') as ThemeColorName;

  // --- Create polygon ---
  const polyStyle = resolveGeometryStyle('polygon', 'Triangle', styleCtx, elementDisplay);
  const borderStyle = resolveGeometryStyle('line', 'Triangle', styleCtx, elementDisplay);
  const polygon = elementFactory.polygon({
    vertices,
    name: element.id,
    vertexNames: ['A', 'B', 'C'],
    style: {
      fillColor: polyStyle.fillColor,
      fillOpacity: polyStyle.fillOpacity,
      strokeWidth: 0,
      vertex: resolveBaseVertexStyle('Triangle', ctx),
      vertexPerIndex: buildVertexPerIndex(TRIANGLE_VERTEX_NAMES, 'Triangle', ctx),
      border: {
        strokeWidth: borderStyle.strokeWidth ?? 2,
        color: borderStyle.color,
      },
      borderPerEdge: {
        0: {
          color: edgeABStyle.color,
          strokeWidth: edgeABStyle.strokeWidth,
          dash: edgeABStyle.dash,
          label: resolveSubpartLabelOverride(
            (elementDisplay?.subparts?.['AB']?.label as LineLabelConfig | undefined) ?? edgeABStyle.label,
            computeEdgeScope('c', givenKeys, geometryConfig, labelColorAB, element.names?.c),
            element.names?.c ?? 'c',
            labelColorAB,
          ),
        },
        1: {
          color: edgeBCStyle.color,
          strokeWidth: edgeBCStyle.strokeWidth,
          dash: edgeBCStyle.dash,
          label: resolveSubpartLabelOverride(
            (elementDisplay?.subparts?.['BC']?.label as LineLabelConfig | undefined) ?? edgeBCStyle.label,
            computeEdgeScope('a', givenKeys, geometryConfig, labelColorBC, element.names?.a),
            element.names?.a ?? 'a',
            labelColorBC,
          ),
        },
        2: {
          color: edgeCAStyle.color,
          strokeWidth: edgeCAStyle.strokeWidth,
          dash: edgeCAStyle.dash,
          label: resolveSubpartLabelOverride(
            (elementDisplay?.subparts?.['CA']?.label as LineLabelConfig | undefined) ?? edgeCAStyle.label,
            computeEdgeScope('b', givenKeys, geometryConfig, labelColorCA, element.names?.b),
            element.names?.b ?? 'b',
            labelColorCA,
          ),
        },
      },
    },
  });

  jsxObjects.push(polygon.polygon, ...polygon.vertices, ...polygon.borders);
  jsxObjects.push(...polygon.helpers);
  labels.push(...polygon.borderLabels);

  // --- Create angle markers ---
  // Winding order: JSXGraph measures CCW from ray(vertex→a) to ray(vertex→b).
  // Triangle vertices are CCW, so [next, vertex, prev] gives the interior angle.
  const angleConfigs = [
    {
      prop: 'alpha' as const,
      extProp: 'extAlpha' as const,
      a: polygon.vertices[1], // B
      vertex: polygon.vertices[0],
      b: polygon.vertices[2], // C
    },
    {
      prop: 'beta' as const,
      extProp: 'extBeta' as const,
      a: polygon.vertices[2], // C
      vertex: polygon.vertices[1],
      b: polygon.vertices[0], // A
    },
    {
      prop: 'gamma' as const,
      extProp: 'extGamma' as const,
      a: polygon.vertices[0], // A
      vertex: polygon.vertices[2],
      b: polygon.vertices[1], // B
    },
  ];

  const angleResults: AngleResult[] = [];
  for (const ac of angleConfigs) {
    const angleRad = solved.angles[ac.prop];
    const isRightAngle = Math.abs(angleRad - Math.PI / 2) < EPSILON;
    const isGivenExterior = givenKeys.has(ac.extProp);

    // For exterior angles, swap a/b to reverse the arc direction
    const effectiveA = isGivenExterior ? ac.b : ac.a;
    const effectiveB = isGivenExterior ? ac.a : ac.b;

    const rightAngleDisplay = geometryConfig.angle?.rightAngleDisplay ?? 'square';
    const angleType = isRightAngle ? (rightAngleDisplay === 'circle-dot' ? 'sector' : 'square') : 'sector';

    const angleName = element.names?.[isGivenExterior ? ac.extProp : ac.prop] ?? ac.prop;

    // 1. Resolve style from 4-layer cascade
    const angleStyle = resolveGeometryStyle('angle', 'Triangle', styleCtx, elementDisplay, ac.prop);
    const angleLabelColor = resolveGeometryLabelColor(
      'Triangle',
      styleCtx,
      elementDisplay,
      ac.prop,
      'angle',
    ) as ThemeColorName;

    // 2. Compute scope-driven visibility & label
    const scopeResult = computeAngleScope(ac, givenKeys, geometryConfig, angleLabelColor, angleName);

    // 3. Resolve final visibility: explicit override > style cascade (includes shape defaults) > default true
    const explicitVisible = elementDisplay?.subparts?.[ac.prop]?.visible;
    const visible = explicitVisible ?? angleStyle.visible ?? true;

    // 4. Resolve label: subpart label override > angle style label > scope label
    //    The subpart's top-level `label` (e.g., subparts.alpha.label) is a direct
    //    display override that takes priority over the angle style cascade's label.
    const subpartLabelOverride = elementDisplay?.subparts?.[ac.prop]?.label as AngleLabelConfig | undefined;
    let scopeLabel = scopeResult.label;
    if (isRightAngle && subpartLabelOverride === undefined && angleStyle.label === undefined) {
      scopeLabel = false;
    }
    const label = resolveAngleLabelOverride(
      subpartLabelOverride ?? angleStyle.label,
      scopeLabel,
      angleName,
      angleLabelColor,
    );

    // 5. ALWAYS create
    const angleResult = elementFactory.angle({
      a: effectiveA,
      vertex: ac.vertex,
      b: effectiveB,
      name: angleName,
      style: {
        fillColor: angleStyle.fillColor,
        fillOpacity: angleStyle.fillOpacity,
        strokeColor: angleStyle.strokeColor,
        type: angleType,
        radius: isRightAngle ? 0.4 : angleStyle.radius,
        visible,
        label,
      },
    });

    angleResults.push(angleResult);
    jsxObjects.push(angleResult.angle);
    jsxObjects.push(...angleResult.helpers);
    labels.push(angleResult.label);
  }

  // --- Build port map ---
  const ports = buildTrianglePorts(polygon, solved, element.id);

  // Named objects for PATCH_DISPLAY targeting (e.g., 't1.edge.BC', 't1.angle.alpha')
  // Always use canonical prop names (alpha/beta/gamma) for stable targeting,
  // independent of custom display names.
  const namedObjects: Record<string, JXGElement> = {
    'point.A': polygon.vertices[0],
    'point.B': polygon.vertices[1],
    'point.C': polygon.vertices[2],
    'edge.AB': polygon.borders[0],
    'edge.BC': polygon.borders[1],
    'edge.CA': polygon.borders[2],
  };
  for (let i = 0; i < angleConfigs.length; i++) {
    namedObjects[`angle.${angleConfigs[i].prop}`] = angleResults[i].angle;
  }

  // Apply per-edge visibility overrides from display config
  const triangleEdgeNames = ['AB', 'BC', 'CA'] as const;
  for (let i = 0; i < triangleEdgeNames.length; i++) {
    if (elementDisplay?.subparts?.[triangleEdgeNames[i]]?.visible === false) {
      (polygon.borders[i] as unknown as { setAttribute: (attrs: Record<string, unknown>) => void }).setAttribute({
        visible: false,
      });
    }
  }

  return {
    jsxObjects,
    labels,
    ports,
    namedObjects,
    controlPoints: freePoints,
    vertexPoints: { A: vertices[0], B: vertices[1], C: vertices[2] },
    destroy: () => {
      const innerBoard = elementFactory.getContext().board;
      for (const label of labels) {
        innerBoard.removeObject(label);
      }
      for (const obj of jsxObjects) {
        innerBoard.removeObject(obj);
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Port registration
// ---------------------------------------------------------------------------

function buildTrianglePorts(polygon: PolygonResult, _solved: SolvedTriangle, _elementId: string): PortMap {
  const [pA, pB, pC] = polygon.vertices as unknown as Array<{ X: () => number; Y: () => number }>;
  const _borders = polygon.borders as unknown as Array<{ L: () => number }>;

  return {
    // Geometric ports - point coordinates
    'point.A': () => [pA.X(), pA.Y()] as PointPort,
    'point.B': () => [pB.X(), pB.Y()] as PointPort,
    'point.C': () => [pC.X(), pC.Y()] as PointPort,

    // Geometric ports - edges
    'edge.AB': () => ({ start: [pA.X(), pA.Y()], end: [pB.X(), pB.Y()] }) as EdgePort,
    'edge.BC': () => ({ start: [pB.X(), pB.Y()], end: [pC.X(), pC.Y()] }) as EdgePort,
    'edge.CA': () => ({ start: [pC.X(), pC.Y()], end: [pA.X(), pA.Y()] }) as EdgePort,

    // Scalar ports - side lengths (reactive)
    a: () => distance(pB, pC),
    b: () => distance(pC, pA),
    c: () => distance(pA, pB),

    // Scalar ports - interior angles (reactive, in radians)
    alpha: () => computeAngle(pB, pA, pC),
    beta: () => computeAngle(pC, pB, pA),
    gamma: () => computeAngle(pA, pC, pB),

    // Scalar ports - exterior angles (reactive, in radians)
    extAlpha: () => Math.PI - computeAngle(pB, pA, pC),
    extBeta: () => Math.PI - computeAngle(pC, pB, pA),
    extGamma: () => Math.PI - computeAngle(pA, pC, pB),

    // Angle center points (along bisector, 0.8 units from vertex)
    'angleCenter.A': () => computeAngleCenter(pA, pB, pC),
    'angleCenter.B': () => computeAngleCenter(pB, pC, pA),
    'angleCenter.C': () => computeAngleCenter(pC, pA, pB),

    // Computed scalars
    area: () => computeTriangleArea(pA, pB, pC),
    perimeter: () => distance(pA, pB) + distance(pB, pC) + distance(pC, pA),
  };
}

// ---------------------------------------------------------------------------
// Math helpers for reactive port values
// ---------------------------------------------------------------------------

function distance(a: { X: () => number; Y: () => number }, b: { X: () => number; Y: () => number }): number {
  const dx = b.X() - a.X();
  const dy = b.Y() - a.Y();
  return Math.sqrt(dx * dx + dy * dy);
}

function computeAngle(
  p1: { X: () => number; Y: () => number },
  vertex: { X: () => number; Y: () => number },
  p2: { X: () => number; Y: () => number },
): number {
  const dx1 = p1.X() - vertex.X();
  const dy1 = p1.Y() - vertex.Y();
  const dx2 = p2.X() - vertex.X();
  const dy2 = p2.Y() - vertex.Y();

  const dot = dx1 * dx2 + dy1 * dy2;
  const cross = dx1 * dy2 - dy1 * dx2;

  return Math.abs(Math.atan2(cross, dot));
}

function computeTriangleArea(
  a: { X: () => number; Y: () => number },
  b: { X: () => number; Y: () => number },
  c: { X: () => number; Y: () => number },
): number {
  return Math.abs((b.X() - a.X()) * (c.Y() - a.Y()) - (c.X() - a.X()) * (b.Y() - a.Y())) / 2;
}

// ---------------------------------------------------------------------------
// Right-angle constraint (Thales' theorem)
// ---------------------------------------------------------------------------

type TriangleVertexName = 'A' | 'B' | 'C';

/** Map angle key to vertex name */
const ANGLE_TO_VERTEX: Record<TriangleAngle, TriangleVertexName> = {
  alpha: 'A',
  beta: 'B',
  gamma: 'C',
};

/**
 * Detect which vertex (if any) has a right angle (π/2).
 */
function detectRightAngle(solved: SolvedTriangle): TriangleVertexName | null {
  for (const angleKey of ['alpha', 'beta', 'gamma'] as const) {
    if (Math.abs(solved.angles[angleKey] - Math.PI / 2) < EPSILON) {
      return ANGLE_TO_VERTEX[angleKey];
    }
  }
  return null;
}

/**
 * Create triangle vertices with Thales' theorem constraint.
 *
 * The right-angle vertex becomes a glider on the Thales circle
 * (circle with the hypotenuse as diameter). By Thales' theorem,
 * any point on this circle forms a 90° angle with the diameter endpoints.
 *
 * The other two vertices are free control points.
 */
function createThalesConstrained(
  board: import('jsxgraph').Board,
  coords: Record<TriangleVertexName, [number, number]>,
  rightAngleAt: TriangleVertexName,
): { vertices: [JXGElement, JXGElement, JXGElement]; helpers: JXGElement[]; freePoints: JXGElement[] } {
  const helperAttrs = { visible: false, strokeWidth: 0, fixed: true, highlight: false, withLabel: false };

  // Map vertex names to their roles
  const names: TriangleVertexName[] = ['A', 'B', 'C'];
  const diameterNames = names.filter((n) => n !== rightAngleAt) as [TriangleVertexName, TriangleVertexName];

  // Create the two diameter endpoints as free control points
  const pDiam0 = board.create('point', coords[diameterNames[0]], {
    ...HIDDEN_POINT_ATTRS,
    name: diameterNames[0],
  }) as JXGElement;
  const pDiam1 = board.create('point', coords[diameterNames[1]], {
    ...HIDDEN_POINT_ATTRS,
    name: diameterNames[1],
  }) as JXGElement;

  // Thales circle: center = midpoint of diameter, passes through both endpoints
  const midpoint = board.create('midpoint', [pDiam0, pDiam1], helperAttrs) as JXGElement;
  const thalesCircle = board.create('circle', [midpoint, pDiam0], helperAttrs) as JXGElement;

  // Right-angle vertex is a glider on the Thales circle
  const rightCoords = coords[rightAngleAt];
  const pRight = board.create('glider', [rightCoords[0], rightCoords[1], thalesCircle], {
    ...HIDDEN_POINT_ATTRS,
    name: rightAngleAt,
  }) as JXGElement;

  // Build vertices array in A, B, C order
  const vertexMap: Record<TriangleVertexName, JXGElement> = {
    [diameterNames[0]]: pDiam0,
    [diameterNames[1]]: pDiam1,
    [rightAngleAt]: pRight,
  } as Record<TriangleVertexName, JXGElement>;

  return {
    vertices: [vertexMap['A'], vertexMap['B'], vertexMap['C']],
    helpers: [midpoint, thalesCircle],
    freePoints: [pDiam0, pDiam1],
  };
}
